/**
 * The Glass — the machine pane (wave glass-4, #800; THE GLASS ruled #786).
 *
 * A sub-route of The Glass: the five actors and how the machine fires — the
 * chassis picture, the trigger map (what fires what, per-workflow Airtable
 * reads/writes), and every non-archived workflow's node-flow page — rendered
 * from the packaged canon (GLASS_CANON.machine + GLASS_CANON.overview,
 * generated at build time from the canon files each section footnotes). The
 * workflow pages and the trigger map are GENERATED canon
 * (generate-workflow-diagrams.py, from the committed n8n exports); this pane
 * reads NO live n8n — each page's own updated date is the freshness it wears.
 *
 * The glass constitution (design-786 §2.10) governs: render canon, never
 * author state — no sentence on this page describes how things stand except
 * canon text carried verbatim; pane prose is mechanical labels only (counts,
 * dates, field names, renders-<source> lines). Freshness is DISPLAYED, never
 * auto-judged: updated dates and state words are the pages' own — no age
 * threshold, no color verdict, no flag. Workflow diagrams render on expand
 * (the largest holds ~47 nodes; nine eager renders is the measured cost
 * avoided).
 */

import { useState } from "react";
import {
  GLASS_CANON,
  type GlassTriggerMapRow,
  type GlassWorkflow,
} from "@command-center/shared";
import { MermaidDiagram } from "@/components/mermaid-diagram";
import { cn } from "@/lib/utils";

export function GlassMachinePage() {
  const { triggerMap, workflows } = GLASS_CANON.machine;

  return (
    <section className="mx-auto max-w-6xl p-6">
      <h1 className="mb-1 text-xl tracking-tight">The Glass — Machine</h1>
      <p className="mb-1 text-sm text-plane-muted">
        The five actors and how the machine fires — the trigger map and every
        non-archived workflow's node flow, rendered read-only from the canon
        files named below.
      </p>
      <p className="mb-5 text-xs text-plane-muted">
        renders <code>{GLASS_CANON.meta.sources.triggerMap}</code> and{" "}
        <code>{GLASS_CANON.meta.sources.workflows}&lt;id&gt;.md</code>, packaged
        at build time; this page renders them read-only.
      </p>

      <ActorsSection />
      <TriggerMapSection triggerMap={triggerMap} />
      <WorkflowsSection workflows={workflows} />
    </section>
  );
}

function SectionHeading({
  label,
  title,
  source,
}: {
  label: string;
  title: string;
  source: string;
}) {
  return (
    <div className="mb-3 mt-10 first:mt-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
        {label}
      </div>
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="text-xs text-plane-muted">
        renders <code>{source}</code>
      </p>
    </div>
  );
}

/** The actors — the chassis picture, verbatim from the overview canon (the
 *  same block the front door renders; one canon, two vantage points). */
function ActorsSection() {
  return (
    <>
      <SectionHeading
        label="Actors"
        title="The chassis, and who touches it"
        source={GLASS_CANON.meta.sources.overview}
      />
      <div className="rounded-md border border-border bg-card p-4">
        <MermaidDiagram source={GLASS_CANON.overview.chassisMermaid} />
      </div>
    </>
  );
}

/** The trigger map — what fires what, per-workflow Airtable reads/writes. */
function TriggerMapSection({
  triggerMap,
}: {
  triggerMap: typeof GLASS_CANON.machine.triggerMap;
}) {
  return (
    <>
      <SectionHeading
        label="Fires"
        title={`The trigger map — ${triggerMap.rows.length} workflows`}
        source={GLASS_CANON.meta.sources.triggerMap}
      />
      <div className="mb-4 overflow-x-auto rounded-md border border-border">
        <table
          className="w-full border-collapse text-left text-xs"
          data-testid="glass-trigger-map"
        >
          <thead>
            <tr className="border-b border-border text-plane-muted">
              <th className="px-2 py-1.5 font-medium">Workflow</th>
              <th className="px-2 py-1.5 font-medium">State</th>
              <th className="px-2 py-1.5 font-medium">Trigger</th>
              <th className="px-2 py-1.5 font-medium">Airtable reads</th>
              <th className="px-2 py-1.5 font-medium">Airtable writes</th>
            </tr>
          </thead>
          <tbody>
            {triggerMap.rows.map((r) => (
              <TriggerMapRow key={r.id} row={r} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-md border border-border bg-card p-4">
        <MermaidDiagram source={triggerMap.mermaid} />
      </div>
    </>
  );
}

function TriggerMapRow({ row }: { row: GlassTriggerMapRow }) {
  return (
    <tr className="border-b border-border/60">
      <td className="px-2 py-1.5">
        <a href={`#workflow-${row.id}`} className="underline">
          {row.name}
        </a>{" "}
        <code className="text-plane-muted">{row.id}</code>
      </td>
      <td className="whitespace-nowrap px-2 py-1.5">{row.state}</td>
      <td className="px-2 py-1.5">{row.trigger}</td>
      <td className="px-2 py-1.5">{row.airtableReads.join(", ")}</td>
      <td className="px-2 py-1.5">{row.airtableWrites.join(", ")}</td>
    </tr>
  );
}

/** One expandable section per workflow; the node-flow diagram renders on
 *  expand. */
function WorkflowsSection({ workflows }: { workflows: GlassWorkflow[] }) {
  return (
    <>
      <SectionHeading
        label="Workflows"
        title={`Node flow — ${workflows.length} pages`}
        source={`${GLASS_CANON.meta.sources.workflows}<id>.md`}
      />
      {workflows.map((w) => (
        <WorkflowCard key={w.id} workflow={w} />
      ))}
    </>
  );
}

function WorkflowCard({ workflow: w }: { workflow: GlassWorkflow }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      id={`workflow-${w.id}`}
      className="mb-3 rounded-md border border-border bg-card"
      data-testid={`glass-workflow-${w.id}`}
    >
      <button
        type="button"
        className={cn(
          "flex w-full items-baseline gap-2 px-4 py-2.5 text-left text-sm hover:bg-muted",
          open && "bg-muted",
        )}
        onClick={() => setOpen(!open)}
      >
        <span className="font-medium">{w.name}</span>
        <code className="text-xs text-plane-muted">{w.id}</code>
        <span className="text-xs text-plane-muted">
          {w.state} · updated {w.updated}
        </span>
        <span className="ml-auto text-xs text-plane-muted">
          {open ? "collapse" : "expand"}
        </span>
      </button>
      {open ? (
        <div className="border-t border-border px-4 py-3">
          {w.description !== null ? (
            <p className="mb-2 text-sm">
              <span className="text-xs font-medium text-plane-muted">
                n8n description (verbatim):{" "}
              </span>
              {w.description}
            </p>
          ) : null}
          <dl className="mb-3 space-y-1 text-xs">
            <MetaField label="Trigger" value={w.trigger} />
            {w.airtableReads.length > 0 ? (
              <MetaField
                label="Airtable reads"
                value={w.airtableReads.join(", ")}
              />
            ) : null}
            {w.airtableWrites.length > 0 ? (
              <MetaField
                label="Airtable writes"
                value={w.airtableWrites.join(", ")}
              />
            ) : null}
            {w.onFailure !== null ? (
              <MetaField label="On failure" value={w.onFailure} />
            ) : null}
            {w.note !== null ? (
              <MetaField label="Note" value={w.note} />
            ) : null}
          </dl>
          <MermaidDiagram source={w.mermaid} />
          <p className="mt-3 text-xs text-plane-muted">
            renders <code>{w.sourcePath}</code>
          </p>
        </div>
      ) : null}
    </div>
  );
}

function MetaField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="inline font-medium text-plane-muted">{label}: </dt>
      <dd className="inline">{value}</dd>
    </div>
  );
}
